// Søk i produktene fra /produkter (nettbutikkens vareliste, GET /produkter?q=, bare for
// ansatte) og velg én variant. Brukes av kalkylekalkulatoren (Calculator.js).
//
// onVelg({ navn, variant, storrelse }) får variantens kostpris og pris; storrelse er satt bare
// når størrelsen faktisk er valgt (ikke når alle størrelsene har samme pris). Har produktet flere varianter
// med ulike priser, velger den ansatte størrelse først. En EAN som treffer nøyaktig én
// variant (f.eks. fra strekkodeleser) velges med en gang.
import React, { useEffect, useState } from 'react';
import Ikon from '../ny-kundesamhandling/Ikon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const kr = (n) => (n == null ? '–' : `${new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 2 }).format(n)} kr`);
const produktNavn = (p) => [p.merke, p.navn, p.fargenavn && `(${p.fargenavn})`].filter(Boolean).join(' ');
const prisNokkel = (v) => `${v.kostpris ?? ''}|${v.pris ?? ''}`;

// «Lager: Røyken 2 · Slemmestad 0» (visLager, brukt i pristilbud).
const lagerTekst = (v) => `Lager: Røyken ${v.lagerRoyken ?? 0} · Slemmestad ${v.lagerSlemmestad ?? 0}`;

export default function ProduktSok({ id = 'produkt-sok', onVelg, visLager = false }) {
  const [sok, setSok] = useState('');
  const [treff, setTreff] = useState([]);
  const [treffFor, setTreffFor] = useState('');
  const [laster, setLaster] = useState(false);
  const [feil, setFeil] = useState(false);
  const [apent, setApent] = useState(null); // produktet som viser variantene sine

  function velg(produkt, variant) {
    const valgtStorrelse = (produkt.varianter || []).length > 1 && new Set(produkt.varianter.map(prisNokkel)).size > 1;
    onVelg({ navn: produktNavn(produkt), produktId: produkt.id ? String(produkt.id) : '', variant, storrelse: valgtStorrelse || produkt.varianter?.length === 1 ? variant.storrelse : '' });
    setSok('');
    setTreff([]);
    setApent(null);
  }

  useEffect(() => {
    const q = sok.trim();
    setApent(null);
    if (q.length < 2) {
      setTreff([]);
      setTreffFor('');
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLaster(true);
      setFeil(false);
      try {
        const r = await fetch(`${API_BASE_URL}/produkter?q=${encodeURIComponent(q)}`, { signal: controller.signal });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        const liste = Array.isArray(data) ? data : [];
        // EAN fra strekkodeleser: én variant med nøyaktig denne EAN-en velges direkte.
        const eanTreff = liste.flatMap((p) => (p.varianter || []).filter((v) => v.ean === q).map((v) => [p, v]));
        if (eanTreff.length === 1) {
          velg(eanTreff[0][0], eanTreff[0][1]);
          return;
        }
        setTreff(liste);
        setTreffFor(q);
      } catch (e) {
        if (e.name !== 'AbortError') {
          setTreff([]);
          setTreffFor(q);
          setFeil(true);
        }
      } finally {
        setLaster(false);
      }
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sok]);

  // Varianter med like priser trengs ikke velges hver for seg.
  function trykkProdukt(p) {
    const varianter = p.varianter || [];
    if (varianter.length === 0) return;
    if (new Set(varianter.map(prisNokkel)).size === 1) velg(p, varianter[0]);
    else setApent(apent === p.id ? null : p.id);
  }

  const harSok = sok.trim().length >= 2;
  const ferdig = harSok && !laster && treffFor === sok.trim();

  return (
    <div>
      <label htmlFor={id} className="mb-1 block font-semibold text-slate-800">Søk i produktene</label>
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
          <Ikon navn="sok" className="w-6 h-6" />
        </span>
        <input
          id={id}
          type="search"
          inputMode="search"
          autoComplete="off"
          value={sok}
          onChange={(e) => setSok(e.target.value)}
          placeholder="Navn, merke, varenummer eller EAN"
          aria-describedby={`${id}-status`}
          className="w-full rounded-xl border-2 border-slate-300 bg-white py-1.5 pl-12 pr-4 text-base focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
        />
      </div>
      <p id={`${id}-status`} role="status" className="mt-1.5 min-h-[1.25rem] text-sm text-slate-600">
        {!harSok
          ? 'Skann strekkoden med strekkodeleseren, eller skriv minst to tegn.'
          : !ferdig
          ? 'Søker …'
          : feil
          ? 'Klarte ikke å søke i produktene. Prøv igjen.'
          : treff.length === 0
          ? 'Fant ingen produkter.'
          : `${treff.length} ${treff.length === 1 ? 'produkt' : 'produkter'}${treff.length === 50 ? ' (de første 50, søk mer nøyaktig)' : ''}`}
      </p>

      {harSok && treff.length > 0 && (
        <ul className="mt-1 max-h-[28rem] divide-y divide-slate-200 overflow-y-auto rounded-xl border border-slate-200 bg-white">
          {treff.map((p) => {
            const varianter = p.varianter || [];
            const flere = new Set(varianter.map(prisNokkel)).size > 1;
            const priser = varianter.map((v) => v.pris).filter((n) => n != null);
            const prisTekst = !priser.length
              ? 'Mangler pris'
              : Math.min(...priser) === Math.max(...priser)
              ? kr(priser[0])
              : `${kr(Math.min(...priser))} – ${kr(Math.max(...priser))}`;
            const panelId = `${id}-var-${p.id}`;
            return (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => trykkProdukt(p)}
                  disabled={!varianter.length}
                  aria-expanded={flere ? apent === p.id : undefined}
                  aria-controls={flere ? panelId : undefined}
                  className="flex w-full items-center gap-2 px-3 py-1.5 min-h-[2.75rem] text-left hover:bg-indigo-50 focus:outline-none focus-visible:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-slate-900 [overflow-wrap:anywhere]">{produktNavn(p)}</span>
                    <span className="block text-sm text-slate-600">
                      {[p.kategori, p.varenummer && `Varenr. ${p.varenummer}`, !varianter.length && 'Ingen varianter'].filter(Boolean).join(' · ')}
                    </span>
                    {visLager && !flere && varianter[0] && (
                      <span className="block text-sm text-slate-600">{lagerTekst(varianter[0])}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-right text-sm">
                    <span className="block font-semibold text-slate-900">{prisTekst}</span>
                    {flere && <span className="block text-slate-600">Velg størrelse</span>}
                  </span>
                  {flere && <Ikon navn="ned" className={`w-5 h-5 shrink-0 text-slate-600 transition-transform ${apent === p.id ? 'rotate-180' : ''}`} />}
                </button>
                {flere && apent === p.id && (
                  <ul id={panelId} className="grid gap-2 bg-slate-50 px-3 py-1.5 sm:grid-cols-2">
                    {varianter.map((v) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          onClick={() => velg(p, v)}
                          className="flex w-full flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-lg border-2 border-slate-200 bg-white px-3 py-1.5 text-left hover:border-indigo-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
                        >
                          <span className="font-semibold text-slate-900">{v.storrelse || 'Uten størrelse'}</span>
                          <span className="text-sm text-slate-700">
                            Kost {kr(v.kostpris)} · Pris {kr(v.pris)}
                          </span>
                          {visLager && <span className="w-full text-sm text-slate-600">{lagerTekst(v)}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
